import { useEffect, useRef, useState } from 'react';
import {
  Image,
  Description,
  Folder,
  PermPhoneMsg,
  GraphicEq,
  MovieCreation,
  Close,
} from '@material-ui/icons';
import { Modal } from '../../../components/Modal';
import { IconButton } from '../../../components/IconButton';
import { Tooltip } from '../../../components/Tooltip';
import { SupportTicketUpdateAttachment } from '../../../types/commonTypes';
import { useSupportTicket } from '../../../hooks/SupportTicket/useSupportTicket';
import { mapTicket } from '../../../hooks/SupportTicket/supportTicketModel';

interface Props {
  attachments: SupportTicketUpdateAttachment[];
  ticketId: string;
  onClose(): void;
  open: boolean;
}

export const ViewAttachmentModal = ({
  attachments,
  ticketId,
  onClose,
  open,
}: Props) => {
  const { getTicket, response, loading, error, isSuccess } =
    useSupportTicket(false);
  const pendingDownload = useRef<{ id: string; tab: Window } | null>(null);
  const [downloadError, setDownloadError] = useState('');

  const closePendingDownload = () => {
    pendingDownload.current?.tab.close();
    pendingDownload.current = null;
  };

  useEffect(() => () => closePendingDownload(), []);

  useEffect(() => {
    const pending = pendingDownload.current;
    if (!pending || loading) return;
    if (error) {
      setDownloadError(String(error.data || error.statusText));
      closePendingDownload();
    } else if (isSuccess && response) {
      const ticket = mapTicket(response);
      const attachment = ticket?.updates
        .flatMap((update) => update.message.attachments)
        .find((item) => item.id === pending.id);
      if (attachment?.url && /^https?:\/\//i.test(attachment.url)) {
        pending.tab.location.replace(attachment.url);
        pendingDownload.current = null;
      } else {
        setDownloadError('This attachment is no longer available.');
        closePendingDownload();
      }
    }
  }, [response, loading, error, isSuccess]);

  const getIcon = (extension: any) => {
    switch (extension) {
      case 'png':
      case 'jpg':
      case 'jpeg':
      case 'gif':
        return <Image />;
      case 'zip':
        return <Folder />;
      case 'recording':
        return <PermPhoneMsg />;
      case 'mp3':
        return <GraphicEq />;
      case 'mov':
      case 'ts':
        return <MovieCreation />;
      default:
        return <Description />;
    }
  };

  const viewFile = (id: string) => {
    if (loading || pendingDownload.current) return;
    setDownloadError('');
    // Open during the click so the browser permits the tab; fetch a fresh signed URL.
    const tab = window.open('about:blank', '_blank');
    if (!tab) {
      setDownloadError('Allow pop-ups to open this attachment.');
      return;
    }
    tab.opener = null;
    pendingDownload.current = { id, tab };
    getTicket(ticketId);
  };

  const handleClose = () => {
    closePendingDownload();
    onClose();
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      portalOut={document.getElementById('ticket-modal') as Element}
      maxWidth="sm"
      hideCloseButton
    >
      <div className="inline-flex items-center justify-between w-full bg-background-dark py-2 pl-4 pr-2">
        <h5 className="text-lg text-white">Attachments</h5>
        <IconButton onClick={handleClose}>
          <Close />
        </IconButton>
      </div>
      {loading ? (
        <p role="status" className="px-4 pt-4">
          Preparing attachment...
        </p>
      ) : null}
      {downloadError ? (
        <p role="alert" className="px-4 pt-4 text-error">
          {downloadError}
        </p>
      ) : null}
      <ul className="flex flex-col p-4 max-h-[400px] overflow-x-auto gap-2">
        {attachments.map((attachment) => {
          const splitPath = attachment.filename.trim().split('.');
          let extension = splitPath[splitPath.length - 1];
          if (extension.includes('call_recording')) extension = 'recording';

          return (
            <li key={attachment.id}>
              <button
                type="button"
                className="flex items-center gap-2 w-full rounded hover:bg-primary disabled:opacity-50"
                disabled={loading}
                onClick={() => viewFile(attachment.id)}
              >
                {getIcon(extension)}
                <Tooltip title="View in new tab" showArrow placement="top">
                  <span className="min-w-[100px] max-w-full overflow-hidden whitespace-nowrap text-ellipsis">
                    {attachment.filename}
                  </span>
                </Tooltip>
              </button>
            </li>
          );
        })}
      </ul>
    </Modal>
  );
};
